import styles from './ui-menu.css';
import { UIElement } from '../../ui-element';
import { UIMenuTab } from './ui-menu-tab';
import { UIMenuColumn } from './ui-menu-column';
import { UIMenuGroup } from './ui-menu-group';
import { UIMenuLink } from './ui-menu-link';
import { Component } from '../../../decorators/component.decorator';
import { Attribute } from '../../../decorators/attribute.decorator';
import { Child, Children } from '../../../decorators/child.decorator';
import { browser, Digest, eventBus } from '../../../global/index';
import { insertElements } from '../../../global/ui-helpers';
import { runInNextFrame, throttle } from '../../../global/helpers';
import { isEscapePressed, isTabPressed } from '../../../global/keyboard';
import {
    handleScrollButtonVisibility,
    handleScrollClickEvent,
    renderButton,
} from '../../tabs/ui-tabs.helpers';
import {
    getMenuColumns,
    getMenuGroups,
    getMenuLinkAnchors,
    getMenuTabs,
} from './ui-menu.helpers';
import type { ScrollContext } from '../../../global/utils.type';
import type { UIMenuLayout } from './ui-menu.type';
import type { UINavToggleEventDetail } from '../ui-navtoggle.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMenu
 * @element ui-menu
 * @classdesc Represents a class for <code>ui-menu</code> element.
 * Used for the main navigational menu.
 * Supports two layouts via the <code>layout</code> attribute:
 * <ul>
 *   <li><b>default</b>: tabs open a popup dropdown with <code>ui-menu-column</code>, <code>ui-menu-group</code> and <code>ui-menu-link</code> elements inside.</li>
 *   <li><b>direct-links</b>: tabs are direct navigation links (each must have an <code>href</code> attribute). No popup is shown.</li>
 * </ul>
 * In <code>default</code> layout, columns are rendered directly under the active tab on desktop and flattened row-major on mobile.
 * @property {UIMenuLayout} [layout="default"] {@attr layout} Layout mode for the menu.
 *   {@desc default: tabs open a popup dropdown with columns and links}
 *   {@desc direct-links: tabs are direct navigation links, no popup}
 * @property {string} [currentId] {@attr current-id} ID of the element that represents the current page.
 * In <code>default</code> layout this should be the ID of a <code>ui-menu-link</code> element.
 * In <code>direct-links</code> layout this should be the ID of a <code>ui-menu-tab</code> element.
 * @property {boolean} [open=false] {@attr open} Whether the menu is open or not. Only applicable in <code>default</code> layout.
 * @property {boolean} [hideChevrons=false] {@attr hide-chevrons} Hides chevron icons on all menu tabs on desktop. Only applicable in <code>default</code> layout.
 * @property {Array<UIMenuTab>} menuTabs Read-only. Array of all <code>ui-menu-tab</code> child elements.
 * @property {HTMLElement} nav Read-only. Reference to the inner <code>&lt;nav&gt;</code> element. Used as the boundary for outside-click detection.
 * @property {HTMLDivElement} menuBar Read-only. Reference to the menu tabs container element.
 * @property {HTMLDivElement} menuTabsContainer Read-only. Reference to the scrollable menu tabs container.
 * @property {HTMLDivElement} menuColumnsContainer Read-only. Reference to the menu columns container. Only populated in <code>default</code> layout.
 * @property {HTMLButtonElement} mobileCTA Read-only. Reference to the mobile CTA button element.
 * @slot mobile-cta Optional slot for a call-to-action element (e.g., <code>ui-buttonbar</code>) that appears fixed at the bottom of the menu on mobile devices.
 * @listens event:menu-click Fired by child <code>ui-menu-link</code> elements when their anchor is clicked.
 * @example
 * <ui-menu current-id="finances-3">
 *   <ui-menu-tab label="Daily Banking">
 *     <ui-menu-column id="2">
 *       <ui-menu-group label="Finances">
 *         <ui-menu-link href="#" id="finances-1">Menu link</ui-menu-link>
 *         <ui-menu-link href="#" id="finances-3">Menu link</ui-menu-link>
 *       </ui-menu-group>
 *     </ui-menu-column>
 *   </ui-menu-tab>
 *   <ui-buttonbar slot="mobile-cta">
 *     <ui-button layout="secondary" href="#">Join Us</ui-button>
 *   </ui-buttonbar>
 * </ui-menu>
 */
@Component({
    tag: 'ui-menu',
    styles,
})
export class UIMenu extends UIElement {
    @Attribute(String) layout: UIMenuLayout = 'default';
    @Attribute() currentId?: string;
    @Attribute(Boolean) open: boolean = false;
    @Attribute(Boolean) hideChevrons: boolean = false;
    @Attribute() for?: string;
    @Attribute() group?: string;

    @Children('ui-menu-tab') menuTabs!: Array<UIMenuTab>;
    @Child('nav') nav!: HTMLElement;
    @Child('.ui-menu-tabs') menuBar!: HTMLDivElement;
    @Child('.ui-menu-tabs__container') menuTabsContainer!: HTMLDivElement;
    @Child('.ui-menu-columns') menuColumnsContainer!: HTMLDivElement;
    @Child('[slot="mobile-cta"] button') mobileCTA!: HTMLButtonElement;

    @Child('.ui-menu__scroll-container[placement="left"] > button')
    scrollButtonLeft?: HTMLButtonElement;
    @Child('.ui-menu__scroll-container[placement="right"] > button')
    scrollButtonRight?: HTMLButtonElement;

    private mobileAction?: HTMLElement;
    private activeMenuTab: UIMenuTab | null = null;

    private columnOwners = new WeakMap<UIMenuColumn, UIMenuTab>();
    private groupOwners = new WeakMap<UIMenuGroup, UIMenuColumn>();
    private isLastRenderedMobile: boolean | null = null;

    private scrollHandler?: () => void;
    private resizeHandler?: () => void;
    private scrollClickHandler?: (event: PointerEvent) => void;
    private outsideClickHandler?: (event: MouseEvent) => void;
    private windowBlurHandler?: () => void;
    private outsideClickAnimationFrame: number | undefined;
    private menuLinkClickHandler?: EventListener;
    private attributeObserver?: MutationObserver;
    private childObserver?: MutationObserver;
    private isSyncingToggle = false;

    private static readonly KEYBOARD_CLICK_DETAIL = 0;
    private static readonly THROTTLE_MS = 100;

    /**
     * Gets the scroll context object used by scroll button visibility handlers.
     */
    private get scrollContext(): ScrollContext {
        return {
            captions: this.menuTabsContainer,
            scrollButtonLeft: this.scrollButtonLeft,
            scrollButtonRight: this.scrollButtonRight,
        };
    }

    private get isDirectLinksLayout(): boolean {
        return this.layout === 'direct-links';
    }

    /**
     * Marks an element as current by ID, reflecting the <code>current-id</code> attribute.
     * Works for both layouts:
     * - In <code>default</code> layout: finds the <code>ui-menu-link</code> with the given ID,
     *   marks it current, and marks its parent <code>ui-menu-tab</code> current too.
     * - In <code>direct-links</code> layout: finds the <code>ui-menu-tab</code> with the given ID
     *   and marks it current.
     * Clears any previously current link/tab first.
     */
    setCurrent(linkId: string) {
        const escapedId = CSS.escape(linkId);

        const link = this.querySelector<UIMenuLink>(
            `ui-menu-link#${escapedId}`
        );
        if (link) {
            this.clearCurrent();
            link.current = true;
            const parentColumn = link.closest<UIMenuColumn>('ui-menu-column');
            const parentTab = parentColumn
                ? (this.columnOwners.get(parentColumn) ?? null)
                : link.closest<UIMenuTab>('ui-menu-tab');
            if (parentTab) {
                parentTab.current = true;
            }
            return;
        }

        const tab = this.querySelector<UIMenuTab>(`ui-menu-tab#${escapedId}`);
        if (tab) {
            this.clearCurrent();
            tab.current = true;
        }
    }

    /**
     * Clears the current marker from all menu links and tabs.
     */
    private clearCurrent(): void {
        this.querySelectorAll<UIMenuLink>('ui-menu-link[current]').forEach(
            (link) => {
                link.current = false;
            }
        );
        this.querySelectorAll<UIMenuTab>('ui-menu-tab[current]').forEach(
            (tab) => {
                tab.current = false;
            }
        );
    }

    /**
     * Applies a menu open/close transition and keeps related state in sync.
     */
    private setMenuState(
        menuTab: UIMenuTab | null,
        emitToggleState: boolean = false
    ): void {
        const wasOpen = this.open || this.activeMenuTab !== null;

        if (!menuTab) {
            this.open = false;
            if (this.activeMenuTab) {
                this.activeMenuTab.expanded = false;
                this.activeMenuTab = null;
            }
            this.detachOutsideClickHandler();

            if (wasOpen && emitToggleState && !this.isSyncingToggle) {
                this.emitToggleState(false);
            }
            return;
        }

        if (this.activeMenuTab && this.activeMenuTab !== menuTab) {
            this.activeMenuTab.expanded = false;
        }

        this.open = true;
        this.activeMenuTab = menuTab;
        this.activeMenuTab.expanded = true;
        this.renderMenuColumns();
        handleScrollButtonVisibility(this.scrollContext);
        if (browser.desktopMediaMatches()) {
            this.attachOutsideClickHandler();
        }
    }

    /**
     * Opens the menu and activates the specified tab.
     */
    openMenu(menuTab?: UIMenuTab) {
        const tabs = getMenuTabs(this.menuTabsContainer);
        const activeMenu =
            menuTab ||
            tabs.find((tab) => tab.expanded) ||
            tabs.find((tab) => tab.current) ||
            tabs[0];
        if (activeMenu) {
            this.setMenuState(activeMenu);
        }
    }

    /**
     * Closes the menu and deactivates the current tab.
     */
    closeMenu() {
        this.setMenuState(null, true);
    }

    /**
     * Toggles the menu open/closed state.
     * If the menu is open, it will be closed. If closed, it will be opened.
     */
    toggleMenu(tabId?: string) {
        if (this.open) {
            this.closeMenu();
        } else {
            tabId ? this.openMenuByTabId(tabId) : this.openMenu();
        }
    }

    /**
     * Opens the menu and activates a tab by its ID.
     */
    openMenuByTabId(tabId: string) {
        const menuTab = getMenuTabs(this.menuTabsContainer).find(
            (tab) => tab.id === tabId
        );
        if (menuTab) {
            this.openMenu(menuTab);
        }
    }

    /**
     * Builds ownership maps for columns and groups to keep track of menu structure.
     * Creates WeakMaps to track which tab owns each column and which column owns each group.
     */
    private buildOwnershipMaps() {
        getMenuTabs(this.menuTabsContainer).forEach((tab) =>
            this.mapTabOwnership(tab)
        );
    }

    /**
     * Records column/group ownership for a single tab.
     * @private
     */
    private mapTabOwnership(tab: UIMenuTab): void {
        getMenuColumns(tab).forEach((col) => {
            this.columnOwners.set(col, tab);
            getMenuGroups(col).forEach((group) => {
                this.groupOwners.set(group, col);
            });
        });
    }

    /**
     * Renders menu columns for the active tab based on current layout (desktop/mobile).
     * For desktop: moves columns directly from tab to container (no cloning).
     * For mobile: moves groups directly into flattened structure (no cloning).
     */
    private renderMenuColumns() {
        const isMobile = !browser.desktopMediaMatches();

        const layoutChanged =
            this.isLastRenderedMobile !== null &&
            this.isLastRenderedMobile !== isMobile;
        if (layoutChanged) {
            this.restoreToOriginalStructure();
        } else {
            this.restoreActiveContent(isMobile);
        }

        this.isLastRenderedMobile = isMobile;

        const menuColumns = getMenuColumns(this.activeMenuTab!);

        if (!menuColumns.length) {
            return;
        }

        if (isMobile) {
            const flattenedGroups = this.flattenGroupsByMoving(menuColumns);
            const mobileColumn = this.createElement({
                tagName: 'ui-menu-column',
                children: flattenedGroups,
            }) as UIMenuColumn;
            this.menuColumnsContainer.append(mobileColumn);

            if (this.mobileAction) {
                this.menuColumnsContainer.append(this.mobileAction);
            }
        } else {
            // Desktop: move columns directly (no cloning)
            menuColumns.forEach((col: UIMenuColumn) => {
                this.menuColumnsContainer.append(col);
            });
        }
    }

    /**
     * Restores active content when switching tabs (same layout).
     * More efficient than full restore when layout hasn't changed.
     */
    private restoreActiveContent(isMobile: boolean) {
        if (isMobile) {
            const groupsInContainer = getMenuGroups(this.menuColumnsContainer);
            groupsInContainer.forEach((group) => {
                const ownerColumn = this.groupOwners.get(group);
                if (ownerColumn) {
                    ownerColumn.append(group);
                }
            });

            if (
                this.mobileAction &&
                this.menuColumnsContainer.contains(this.mobileAction)
            ) {
                this.append(this.mobileAction);
            }
            this.menuColumnsContainer.replaceChildren();
        } else {
            const columnsInContainer = getMenuColumns(
                this.menuColumnsContainer
            );
            columnsInContainer.forEach((col) => {
                const ownerTab = this.columnOwners.get(col);
                if (ownerTab) {
                    ownerTab.append(col);
                }
            });
        }
    }

    /**
     * Restores everything to original structure when layout changes (desktop ↔ mobile).
     * Moves both columns and groups back to their original positions.
     */
    private restoreToOriginalStructure() {
        if (
            this.mobileAction &&
            this.menuColumnsContainer.contains(this.mobileAction)
        ) {
            this.append(this.mobileAction);
        }

        const groupsInContainer = getMenuGroups(this.menuColumnsContainer);
        groupsInContainer.forEach((group) => {
            const ownerColumn = this.groupOwners.get(group);
            if (ownerColumn) {
                ownerColumn.append(group);
            }
        });

        const columnsInContainer = getMenuColumns(this.menuColumnsContainer);
        columnsInContainer.forEach((col) => {
            const ownerTab = this.columnOwners.get(col);
            if (ownerTab) {
                col.active = false;
                ownerTab.append(col);
            } else {
                col.remove();
            }
        });
    }

    /**
     * Flattens columns in row-major order for mobile by moving groups directly (no cloning).
     * Groups are extracted from their columns and moved to the flattened structure.
     */
    private flattenGroupsByMoving(
        columns: Array<UIMenuColumn>
    ): Array<UIMenuGroup> {
        if (!columns.length) {
            return [];
        }

        const maxRows = Math.max(
            ...Array.from(columns).map((col) => getMenuGroups(col).length)
        );
        const result: Array<UIMenuGroup> = [];

        for (let row = 0; row < maxRows; row++) {
            for (const col of columns) {
                const group = getMenuGroups(col)[row];
                if (group) {
                    result.push(group);
                }
            }
        }

        return result;
    }

    /**
     * Attaches outside-click and window-blur handlers to close the menu when
     * the user interacts outside it.
     * The window blur listener handles the iframe edge case: clicks inside an
     * iframe do not bubble to the parent document, but they do cause the parent
     * window to lose focus.
     */
    private attachOutsideClickHandler() {
        if (this.outsideClickAnimationFrame !== undefined) {
            return;
        }

        this.outsideClickAnimationFrame = runInNextFrame(() => {
            this.outsideClickAnimationFrame = undefined;
            if (
                !this.isConnected ||
                !this.open ||
                !browser.desktopMediaMatches()
            ) {
                return;
            }

            if (!this.outsideClickHandler) {
                this.outsideClickHandler = (event: MouseEvent) => {
                    if (!this.nav?.contains(event.target as HTMLElement)) {
                        this.closeMenu();
                    }
                };
            }
            document.addEventListener('click', this.outsideClickHandler);

            if (!this.windowBlurHandler) {
                this.windowBlurHandler = () => this.closeMenu();
            }
            window.addEventListener('blur', this.windowBlurHandler);
        });
    }

    /**
     * Detaches the outside-click and window-blur handlers.
     */
    private detachOutsideClickHandler() {
        if (this.outsideClickAnimationFrame !== undefined) {
            cancelAnimationFrame(this.outsideClickAnimationFrame);
            this.outsideClickAnimationFrame = undefined;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
        }
        if (this.windowBlurHandler) {
            window.removeEventListener('blur', this.windowBlurHandler);
        }
    }

    /**
     * Handles menu tab click events, toggling menu open/close on desktop.
     * On desktop, clicking an active tab closes the menu. Clicking any tab when
     * menu is closed or clicking a different tab opens the menu with that tab active.
     * Also handles keyboard activation by focusing the first link.
     * Only called in <code>default</code> layout — <code>direct-links</code> menus do not attach this handler.
     */
    private handleMenuTabClick(menuTab: UIMenuTab, event: Event) {
        if (menuTab.isLink) {
            return;
        }

        if (
            browser.desktopMediaMatches() &&
            this.open &&
            this.activeMenuTab === menuTab
        ) {
            this.closeMenu();
        } else {
            this.openMenu(menuTab);

            if (this.isKeyboardEvent(event)) {
                this.focusFirstLink();
            }
        }
    }

    /**
     * Delegates tab clicks so tabs added after hydration need no individual listeners.
     */
    private handleTabsClick = (event: Event): void => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const menuTab = target.closest<UIMenuTab>('ui-menu-tab');
        if (!menuTab || !this.menuTabsContainer.contains(menuTab)) {
            return;
        }

        this.handleMenuTabClick(menuTab, event);
    };

    /**
     * Checks if an event was triggered by keyboard interaction.
     * Detects keyboard events (Enter/Space keys) or click events with detail=0
     * which indicates they were triggered by keyboard activation of a button.
     * @param {Event} event - The event to check
     * @returns {boolean} True if the event was triggered by keyboard, false otherwise
     */
    private isKeyboardEvent(event: Event): boolean {
        if (event instanceof KeyboardEvent) {
            return event.key === 'Enter' || event.key === ' ';
        }

        if (event instanceof PointerEvent || event instanceof MouseEvent) {
            return event.detail === UIMenu.KEYBOARD_CLICK_DETAIL;
        }

        return false;
    }

    /**
     * Focuses the first link in the active menu dropdown.
     * Uses runInNextFrame to ensure the menu is fully rendered before focusing.
     */
    private focusFirstLink(): void {
        runInNextFrame(() => {
            const firstLink =
                this.menuColumnsContainer.querySelector<UIMenuLink>(
                    'ui-menu-link .ui-menu-link__link a'
                );
            firstLink?.focus();
        });
    }

    /**
     * Handles Escape key press by closing the menu and returning focus to the active tab button.
     */
    private handleEscape(): void {
        this.activeMenuTab?.focusTarget?.focus();
        this.closeMenu();
    }

    /**
     * Handles Tab key navigation within the menu.
     */
    private handleTabKey(evt: KeyboardEvent): void {
        if (!this.activeMenuTab) {
            return;
        }

        const menuLinks = getMenuLinkAnchors(this.menuColumnsContainer);
        if (menuLinks.length === 0) {
            return;
        }

        const activeElement = document.activeElement;
        const firstLink = menuLinks[0];
        const lastLink = menuLinks[menuLinks.length - 1];
        const menuTabs = getMenuTabs(this.menuTabsContainer);
        const currentTabIndex = menuTabs.indexOf(this.activeMenuTab);
        const isLastTab = currentTabIndex === menuTabs.length - 1;

        const focusedTab = menuTabs.find(
            (tab) => tab.focusTarget === activeElement
        );
        const focusedTabIndex = focusedTab ? menuTabs.indexOf(focusedTab) : -1;

        // Shift+Tab on first link → return to current menu tab focus target
        if (evt.shiftKey && activeElement === firstLink) {
            evt.preventDefault();
            this.activeMenuTab.focusTarget?.focus();
        }
        // Tab on last link of last tab → let browser handle (focus next element in DOM)
        else if (!evt.shiftKey && activeElement === lastLink && isLastTab) {
            // Don't prevent default - let browser focus next focusable element
            this.closeMenu();
            return;
        }
        // Tab on last link of non-last tab → move to next menu tab
        else if (!evt.shiftKey && activeElement === lastLink) {
            evt.preventDefault();
            const nextTab = menuTabs[currentTabIndex + 1];
            nextTab.focusTarget?.focus();
        }
        // Tab on last menu tab focus target (forward) → close menu and let browser focus next element
        else if (!evt.shiftKey && focusedTabIndex === menuTabs.length - 1) {
            this.closeMenu();
            return;
        }
        // Shift+Tab on any menu tab focus target → move to previous menu tab or close menu if it's the first tab
        else if (evt.shiftKey && focusedTabIndex !== -1) {
            if (focusedTabIndex === 0) {
                this.closeMenu();
            } else {
                evt.preventDefault();
                const prevTab = menuTabs[focusedTabIndex - 1];
                prevTab.focusTarget?.focus();
            }
        }
        // Tab on active menu tab focus target when menu is open → focus first link
        else if (
            !evt.shiftKey &&
            activeElement === this.activeMenuTab.focusTarget
        ) {
            evt.preventDefault();
            firstLink.focus();
        }
    }

    /**
     * Handles keyboard events for navigation within the menu.
     */
    private handleKeydown = (evt: KeyboardEvent) => {
        if (!this.open) {
            return;
        }

        if (isEscapePressed(evt)) {
            this.handleEscape();
        } else if (isTabPressed(evt)) {
            this.handleTabKey(evt);
        }
    };

    /**
     * Re-renders the open menu when the desktop media query changes.
     */
    private handleDesktopMediaChange = () => {
        handleScrollButtonVisibility(this.scrollContext);

        if (this.open && this.activeMenuTab) {
            this.renderMenuColumns();
            if (browser.desktopMediaMatches()) {
                this.attachOutsideClickHandler();
            } else {
                this.detachOutsideClickHandler();
            }
        }
    };

    /**
     * Responds to menu-toggle events from a paired ui-navtoggle.
     * @private
     */
    private handleMenuToggle = (
        event: CustomEvent<Partial<UINavToggleEventDetail>>
    ) => {
        if (this.isSyncingToggle || this.isDirectLinksLayout) {
            return;
        }
        const detail = event.detail ?? {};
        const scoped = detail.for != null || detail.group != null;
        if (scoped) {
            const matches = detail.group
                ? detail.group === this.group
                : detail.for === this.for;
            if (!matches) {
                return;
            }
        } else if (detail.active) {
            return;
        }

        this.isSyncingToggle = true;
        if (detail.active) {
            if (!browser.desktopMediaMatches()) {
                this.openMenu();
            }
        } else {
            this.closeMenu();
        }
        this.isSyncingToggle = false;
    };

    /**
     * Broadcasts the menu's open/closed state on the menu-toggle bus so the
     * paired ui-navtoggle can reflect it. Guarded so the echoed event this
     * dispatch produces is ignored by handleMenuToggle.
     * @private
     */
    private emitToggleState(active: boolean): void {
        this.isSyncingToggle = true;
        eventBus.dispatchCustomEvent('menu-toggle', {
            active,
            for: this.for,
            group: this.group,
        });
        this.isSyncingToggle = false;
    }

    /**
     * Sets up event listeners for menu interactions.
     */
    private setupEventListeners() {
        if (!this.isDirectLinksLayout) {
            this.menuTabsContainer.addEventListener(
                'click',
                this.handleTabsClick
            );
            this.addEventListener('keydown', this.handleKeydown);

            this.menuLinkClickHandler = () => this.closeMenu();
            this.addEventListener('menu-click', this.menuLinkClickHandler);
        }

        this.scrollClickHandler = (event: PointerEvent) => {
            handleScrollClickEvent(event, this.scrollContext, 'ui-menu');
        };

        this.scrollHandler = throttle(() => {
            handleScrollButtonVisibility(this.scrollContext);
        }, UIMenu.THROTTLE_MS);
        this.resizeHandler = throttle(() => {
            handleScrollButtonVisibility(this.scrollContext);
        }, UIMenu.THROTTLE_MS);

        browser.desktopMedia.addEventListener(
            'change',
            this.handleDesktopMediaChange
        );
        window.addEventListener('resize', this.resizeHandler);
        this.menuTabsContainer.addEventListener('scroll', this.scrollHandler);

        this.scrollButtonLeft?.addEventListener(
            'click',
            this.scrollClickHandler
        );
        this.scrollButtonRight?.addEventListener(
            'click',
            this.scrollClickHandler
        );
    }

    /**
     * Removes event listeners for menu interactions.
     */
    private disconnectEventListeners() {
        this.removeEventListener('keydown', this.handleKeydown);
        this.menuTabsContainer?.removeEventListener(
            'click',
            this.handleTabsClick
        );
        if (this.menuLinkClickHandler) {
            this.removeEventListener('menu-click', this.menuLinkClickHandler);
        }
        this.detachOutsideClickHandler();
        browser.desktopMedia.removeEventListener(
            'change',
            this.handleDesktopMediaChange
        );
        if (this.scrollHandler) {
            this.menuTabsContainer?.removeEventListener(
                'scroll',
                this.scrollHandler
            );
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.scrollClickHandler) {
            this.scrollButtonLeft?.removeEventListener(
                'click',
                this.scrollClickHandler
            );
            this.scrollButtonRight?.removeEventListener(
                'click',
                this.scrollClickHandler
            );
        }
    }

    /**
     * Handles attribute mutations observed via MutationObserver.
     * @private
     */
    private handleAttributeMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            const newValue = this.getAttribute(mutation.attributeName!);
            switch (mutation.attributeName) {
                case 'current-id':
                    if (newValue) {
                        this.setCurrent(newValue);
                    } else {
                        this.clearCurrent();
                    }
                    break;
                case 'open':
                    if (!this.isDirectLinksLayout) {
                        const shouldBeOpen = newValue !== null;
                        // Attribute reflection updates this.open before this observer
                        // runs, so activeMenuTab identifies whether a transition remains.
                        if (shouldBeOpen && !this.activeMenuTab) {
                            this.openMenu();
                        } else if (!shouldBeOpen && this.activeMenuTab) {
                            this.closeMenu();
                        }
                    }
                    break;
                default:
                    break;
            }
        }
    }

    /**
     * Moves the light-DOM menu tabs into the scroll container once, in document order.
     * Called during render() for the initial placement.
     */
    private buildMenuTabs(): void {
        getMenuTabs(this).forEach((tab) =>
            this.menuTabsContainer.appendChild(tab)
        );
    }

    /**
     * Handles child mutations observed via MutationObserver.
     * @private
     */
    private handleChildMutations(mutations: Array<MutationRecord>): void {
        const addedTabs: Array<UIMenuTab> = [];
        const removedTabs: Array<UIMenuTab> = [];
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof UIMenuTab && node.parentElement === this) {
                    addedTabs.push(node);
                }
            }
            for (const node of mutation.removedNodes) {
                if (node instanceof UIMenuTab && !this.contains(node)) {
                    removedTabs.push(node);
                }
            }
        }

        if (!addedTabs.length && !removedTabs.length) {
            return;
        }

        removedTabs.forEach((tab) => {
            if (tab === this.activeMenuTab) {
                this.closeMenu();
            }
        });
        addedTabs.forEach((tab) => {
            this.menuTabsContainer.appendChild(tab);
            if (!this.isDirectLinksLayout) {
                this.mapTabOwnership(tab);
            }
        });

        handleScrollButtonVisibility(this.scrollContext);
        if (this.currentId) {
            this.setCurrent(this.currentId);
        }
    }

    /**
     * Observes child changes on both the host (framework tab insertions) and the
     * scroll container (tab removals) with the single child observer.
     * @private
     */
    private observeChildren(): void {
        this.childObserver?.observe(this, { childList: true });
        this.childObserver?.observe(this.menuTabsContainer, {
            childList: true,
        });
    }

    /**
     * @inheritDoc
     */
    render() {
        const mobileCta = this.querySelector<HTMLElement>(
            '[slot="mobile-cta"]'
        );
        if (mobileCta) {
            this.mobileAction = mobileCta;
        }

        const tabsId = Digest.randomId(8, 'menu-tabs-');
        insertElements(this, [
            {
                tagName: 'nav',
                children: [
                    {
                        tagName: 'div',
                        classList: { 'ui-menu-tabs': true },
                        children: [
                            renderButton('left', tabsId, 'ui-menu'),
                            {
                                tagName: 'div',
                                classList: { 'ui-menu-tabs__container': true },
                                attributes: {
                                    id: tabsId,
                                    role: 'menubar',
                                },
                            },
                            renderButton('right', tabsId, 'ui-menu'),
                        ],
                    },
                    {
                        tagName: 'div',
                        classList: { 'ui-menu-columns': true },
                    },
                ],
            },
        ]);

        this.buildMenuTabs();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        if (!this.isDirectLinksLayout) {
            this.buildOwnershipMaps();
        }

        this.setupEventListeners();

        this.attributeObserver = new MutationObserver(
            this.handleAttributeMutations.bind(this)
        );
        this.attributeObserver.observe(this, {
            attributes: true,
            attributeFilter: ['current-id', 'open'],
        });

        this.childObserver = new MutationObserver(
            this.handleChildMutations.bind(this)
        );
        this.observeChildren();

        eventBus.addEventListener(
            'menu-toggle',
            this.handleMenuToggle as EventListener
        );

        if (this.currentId) {
            this.setCurrent(this.currentId);
        }

        if (!this.isDirectLinksLayout && this.open) {
            this.openMenu();
        } else {
            handleScrollButtonVisibility(this.scrollContext);
        }
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.disconnectEventListeners();
        this.attributeObserver?.disconnect();
        this.childObserver?.disconnect();
        eventBus.removeEventListener(
            'menu-toggle',
            this.handleMenuToggle as EventListener
        );
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.setupEventListeners();
        this.attributeObserver?.observe(this, {
            attributes: true,
            attributeFilter: ['current-id', 'open'],
        });
        this.observeChildren();
        eventBus.addEventListener(
            'menu-toggle',
            this.handleMenuToggle as EventListener
        );
    }
}
